import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import {
  Plus, Pencil, Trash2, Save, ChevronUp, ChevronDown,
  AlertTriangle, XCircle, Loader2, RotateCcw, Shield
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTION_NAMES = {
  A: 'Role & Eligibility',
  B: 'Availability & Logistics',
  C: 'Experience & Qualifications',
  D: 'Culture Fit & Mission Alignment',
  E: 'Commitment & Communication',
};

const SECTION_COLORS = {
  A: 'border-l-purple-500 bg-purple-50/30',
  B: 'border-l-blue-500 bg-blue-50/30',
  C: 'border-l-emerald-500 bg-emerald-50/30',
  D: 'border-l-amber-500 bg-amber-50/30',
  E: 'border-l-rose-500 bg-rose-50/30',
};

const EMPTY_QUESTION = {
  question_id: '',
  section: 'A',
  question: '',
  type: 'radio',
  options: ['Yes', 'No'],
  required: true,
  position_types: ['All'],
  disqualify: '',
  disqualify_message: '',
  flag_trigger: null,
  min_val: null,
  max_val: null,
  min_length: null,
  placeholder: '',
};

const ScreeningQuestionsManager = () => {
  const { isOwner } = useAuth();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [resetConfirm, setResetConfirm] = useState(false);
  const [teamCanEdit, setTeamCanEdit] = useState(false);
  const [optionsText, setOptionsText] = useState('');

  useEffect(() => {
    fetchQuestions();
    fetchPermission();
  }, []);

  const fetchQuestions = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/screening-questions`);
      setQuestions(res.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('You don\'t have permission to manage screening questions');
      } else {
        toast.error('Failed to load questions');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchPermission = async () => {
    try {
      const res = await axios.get(`${API}/screening-questions/permission`);
      setTeamCanEdit(res.data.team_can_edit);
    } catch {}
  };

  const togglePermission = async () => {
    try {
      await axios.put(`${API}/screening-questions/permission`, { team_can_edit: !teamCanEdit });
      setTeamCanEdit(!teamCanEdit);
      toast.success(`Team members ${!teamCanEdit ? 'can now' : 'can no longer'} edit questions`);
    } catch {
      toast.error('Failed to update permission');
    }
  };

  const openAddDialog = () => {
    setEditingQuestion({ ...EMPTY_QUESTION, _isNew: true });
    setOptionsText('Yes\nNo');
    setEditDialog(true);
  };

  const openEditDialog = (q) => {
    setEditingQuestion({ ...q });
    setOptionsText((q.options || []).join('\n'));
    setEditDialog(true);
  };

  const saveQuestion = async () => {
    const q = editingQuestion;
    if (!q.question_id || !q.question) {
      toast.error('Question ID and text are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...q,
        options: optionsText.split('\n').map(o => o.trim()).filter(Boolean),
        disqualify: q.disqualify || null,
        disqualify_message: q.disqualify_message || null,
      };
      delete payload._isNew;
      delete payload.id;
      delete payload.created_at;
      delete payload.updated_at;
      delete payload.active;

      if (q._isNew) {
        await axios.post(`${API}/screening-questions`, payload);
        toast.success('Question added');
      } else {
        await axios.put(`${API}/screening-questions/${q.id}`, payload);
        toast.success('Question updated');
      }
      setEditDialog(false);
      fetchQuestions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save question');
    } finally {
      setSaving(false);
    }
  };

  const deleteQuestion = async (id) => {
    try {
      await axios.delete(`${API}/screening-questions/${id}`);
      toast.success('Question deleted');
      setDeleteConfirm(null);
      fetchQuestions();
    } catch {
      toast.error('Failed to delete question');
    }
  };

  const moveQuestion = async (questionId, direction) => {
    const idx = questions.findIndex(q => q.id === questionId);
    if (idx < 0) return;
    const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= questions.length) return;

    const newOrder = [...questions];
    [newOrder[idx], newOrder[targetIdx]] = [newOrder[targetIdx], newOrder[idx]];
    setQuestions(newOrder);

    try {
      await axios.post(`${API}/screening-questions/reorder`, {
        question_ids: newOrder.map(q => q.id)
      });
    } catch {
      toast.error('Failed to reorder');
      fetchQuestions();
    }
  };

  const resetToDefaults = async () => {
    try {
      await axios.post(`${API}/screening-questions/seed-defaults`);
      toast.success('Questions reset to defaults');
      setResetConfirm(false);
      fetchQuestions();
    } catch {
      toast.error('Failed to reset');
    }
  };

  const groupedBySection = questions.reduce((acc, q) => {
    const s = q.section || 'A';
    if (!acc[s]) acc[s] = [];
    acc[s].push(q);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-[#4B2D6E]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="screening-questions-manager">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <p className="text-sm text-[#666]">{questions.length} questions across {Object.keys(groupedBySection).length} sections</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button onClick={openAddDialog} className="bg-[#E8722A] hover:bg-[#D6621F]" data-testid="add-question-btn">
            <Plus className="mr-2 h-4 w-4" /> Add Question
          </Button>
          <Button onClick={() => setResetConfirm(true)} variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" data-testid="reset-defaults-btn">
            <RotateCcw className="mr-2 h-4 w-4" /> Reset to Defaults
          </Button>
        </div>
      </div>

      {/* Team Permission Toggle (Owner only) */}
      {isOwner && (
        <Card className="bg-[#F9FAFB]">
          <CardContent className="py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Shield className="h-5 w-5 text-[#4B2D6E]" />
              <div>
                <p className="font-medium text-sm text-[#333]">Allow Team Members to edit screening questions</p>
                <p className="text-xs text-[#666]">When enabled, Team Members can add, edit, and reorder questions</p>
              </div>
            </div>
            <Switch checked={teamCanEdit} onCheckedChange={togglePermission} data-testid="team-edit-permission-toggle" />
          </CardContent>
        </Card>
      )}

      {/* Questions by Section */}
      {Object.entries(SECTION_NAMES).map(([sectionKey, sectionName]) => {
        const sectionQuestions = groupedBySection[sectionKey] || [];
        return (
          <Card key={sectionKey} className={`border-l-4 ${SECTION_COLORS[sectionKey]}`}>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center justify-between">
                <span>Section {sectionKey} — {sectionName}</span>
                <Badge variant="outline">{sectionQuestions.length} questions</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {sectionQuestions.length === 0 ? (
                <p className="text-sm text-[#999] py-2">No questions in this section</p>
              ) : (
                sectionQuestions.map((q, idx) => (
                  <div
                    key={q.id}
                    className="flex items-start gap-3 p-3 bg-white rounded-lg border hover:border-[#4B2D6E]/30 transition-colors"
                    data-testid={`question-row-${q.question_id}`}
                  >
                    {/* Reorder buttons */}
                    <div className="flex flex-col gap-0.5 pt-1">
                      <button
                        onClick={() => moveQuestion(q.id, 'up')}
                        className="p-0.5 text-gray-400 hover:text-[#4B2D6E] disabled:opacity-30"
                        disabled={idx === 0}
                        data-testid={`move-up-${q.question_id}`}
                      >
                        <ChevronUp className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => moveQuestion(q.id, 'down')}
                        className="p-0.5 text-gray-400 hover:text-[#4B2D6E] disabled:opacity-30"
                        disabled={idx === sectionQuestions.length - 1}
                        data-testid={`move-down-${q.question_id}`}
                      >
                        <ChevronDown className="h-4 w-4" />
                      </button>
                    </div>

                    {/* Question content */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#333] leading-snug">{q.question.replace(/\{\{company_name\}\}/g, '[Company Name]')}</p>
                      <div className="flex flex-wrap items-center gap-2 mt-1.5">
                        <Badge variant="outline" className="text-xs">{q.type}</Badge>
                        {q.required && <Badge className="bg-blue-100 text-blue-700 text-xs">Required</Badge>}
                        {q.disqualify && (
                          <Badge className="bg-red-100 text-red-700 text-xs">
                            <XCircle className="h-3 w-3 mr-1" />
                            Disqualifies: "{q.disqualify}"
                          </Badge>
                        )}
                        {q.flag_trigger && (
                          <Badge className="bg-amber-100 text-amber-700 text-xs">
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            Has Flag Trigger
                          </Badge>
                        )}
                        {q.position_types && !q.position_types.includes('All') && (
                          <Badge className="bg-purple-100 text-purple-700 text-xs">
                            {q.position_types.join(', ')} only
                          </Badge>
                        )}
                      </div>
                      {q.options && q.options.length > 0 && (
                        <p className="text-xs text-[#999] mt-1 truncate">
                          Options: {q.options.join(' | ')}
                        </p>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-1 flex-shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => openEditDialog(q)} data-testid={`edit-${q.question_id}`}>
                        <Pencil className="h-4 w-4 text-[#4B2D6E]" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setDeleteConfirm(q)} data-testid={`delete-${q.question_id}`}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        );
      })}

      {/* Add/Edit Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingQuestion?._isNew ? 'Add New Question' : 'Edit Question'}</DialogTitle>
            <DialogDescription>Configure the screening question settings below</DialogDescription>
          </DialogHeader>
          {editingQuestion && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Question ID (slug)</Label>
                  <Input
                    value={editingQuestion.question_id}
                    onChange={(e) => setEditingQuestion(prev => ({ ...prev, question_id: e.target.value.replace(/\s/g, '_').toLowerCase() }))}
                    placeholder="e.g., driver_license"
                    data-testid="question-id-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Section</Label>
                  <Select value={editingQuestion.section} onValueChange={(v) => setEditingQuestion(prev => ({ ...prev, section: v }))}>
                    <SelectTrigger data-testid="question-section-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(SECTION_NAMES).map(([key, name]) => (
                        <SelectItem key={key} value={key}>Section {key} — {name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-1">
                <Label>Question Text</Label>
                <Textarea
                  value={editingQuestion.question}
                  onChange={(e) => setEditingQuestion(prev => ({ ...prev, question: e.target.value }))}
                  placeholder="Enter your question..."
                  rows={3}
                  data-testid="question-text-input"
                />
                <p className="text-xs text-[#999]">Use {'{{company_name}}'} to insert the company name dynamically</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Question Type</Label>
                  <Select value={editingQuestion.type} onValueChange={(v) => setEditingQuestion(prev => ({ ...prev, type: v }))}>
                    <SelectTrigger data-testid="question-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="radio">Single Choice (Radio)</SelectItem>
                      <SelectItem value="checkbox">Multiple Choice (Checkbox)</SelectItem>
                      <SelectItem value="textarea">Text (Textarea)</SelectItem>
                      <SelectItem value="number">Number</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Position Types</Label>
                  <Select
                    value={editingQuestion.position_types?.includes('All') ? 'All' : editingQuestion.position_types?.[0] || 'All'}
                    onValueChange={(v) => setEditingQuestion(prev => ({ ...prev, position_types: [v] }))}
                  >
                    <SelectTrigger data-testid="question-position-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="All">All Positions</SelectItem>
                      <SelectItem value="Companion">Companion Only</SelectItem>
                      <SelectItem value="CHHA">CHHA Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {(editingQuestion.type === 'radio' || editingQuestion.type === 'checkbox') && (
                <div className="space-y-1">
                  <Label>Answer Options (one per line)</Label>
                  <Textarea
                    value={optionsText}
                    onChange={(e) => setOptionsText(e.target.value)}
                    rows={4}
                    placeholder="Yes&#10;No&#10;Maybe"
                    data-testid="question-options-input"
                  />
                </div>
              )}

              {editingQuestion.type === 'textarea' && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label>Min Characters</Label>
                    <Input
                      type="number"
                      value={editingQuestion.min_length || ''}
                      onChange={(e) => setEditingQuestion(prev => ({ ...prev, min_length: e.target.value ? parseInt(e.target.value) : null }))}
                      data-testid="question-minlength-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>Placeholder</Label>
                    <Input
                      value={editingQuestion.placeholder || ''}
                      onChange={(e) => setEditingQuestion(prev => ({ ...prev, placeholder: e.target.value }))}
                      data-testid="question-placeholder-input"
                    />
                  </div>
                </div>
              )}

              {editingQuestion.type === 'number' && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label>Min Value</Label>
                    <Input
                      type="number"
                      value={editingQuestion.min_val ?? ''}
                      onChange={(e) => setEditingQuestion(prev => ({ ...prev, min_val: e.target.value ? parseInt(e.target.value) : null }))}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>Max Value</Label>
                    <Input
                      type="number"
                      value={editingQuestion.max_val ?? ''}
                      onChange={(e) => setEditingQuestion(prev => ({ ...prev, max_val: e.target.value ? parseInt(e.target.value) : null }))}
                    />
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between p-3 bg-[#F5F5F5] rounded-lg">
                <div>
                  <p className="text-sm font-medium">Required</p>
                  <p className="text-xs text-[#666]">Candidate must answer this question</p>
                </div>
                <Switch
                  checked={editingQuestion.required}
                  onCheckedChange={(v) => setEditingQuestion(prev => ({ ...prev, required: v }))}
                  data-testid="question-required-toggle"
                />
              </div>

              {editingQuestion.type === 'radio' && (
                <div className="space-y-3 p-3 bg-red-50/50 rounded-lg border border-red-100">
                  <p className="text-sm font-medium text-red-700 flex items-center gap-2">
                    <XCircle className="h-4 w-4" /> Disqualification
                  </p>
                  <div className="space-y-1">
                    <Label className="text-xs">Disqualifying Answer (leave empty for none)</Label>
                    <Input
                      value={editingQuestion.disqualify || ''}
                      onChange={(e) => setEditingQuestion(prev => ({ ...prev, disqualify: e.target.value }))}
                      placeholder="e.g., No"
                      data-testid="question-disqualify-input"
                    />
                  </div>
                  {editingQuestion.disqualify && (
                    <div className="space-y-1">
                      <Label className="text-xs">Disqualification Message</Label>
                      <Input
                        value={editingQuestion.disqualify_message || ''}
                        onChange={(e) => setEditingQuestion(prev => ({ ...prev, disqualify_message: e.target.value }))}
                        placeholder="We're sorry, this position requires..."
                        data-testid="question-disqualify-msg-input"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(false)}>Cancel</Button>
            <Button onClick={saveQuestion} disabled={saving} className="bg-[#4B2D6E] hover:bg-[#3A1F5C]" data-testid="save-question-btn">
              <Save className="mr-2 h-4 w-4" />
              {saving ? 'Saving...' : 'Save Question'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={(open) => { if (!open) setDeleteConfirm(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Screening Question</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this question? Candidates will no longer see it in the screening flow.
              <div className="mt-2 p-2 bg-[#F5F5F5] rounded text-sm">
                "{deleteConfirm?.question?.substring(0, 100)}..."
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => deleteQuestion(deleteConfirm?.id)}>
              Delete Question
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset Confirmation */}
      <AlertDialog open={resetConfirm} onOpenChange={setResetConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset to Default Questions</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete ALL current questions and restore the original 33 default questions. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={resetToDefaults}>
              Reset All Questions
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ScreeningQuestionsManager;
